import ResizeIcon from "@mui/icons-material/DragHandle";
import { Box, Paper, alpha, styled } from "@mui/material";

import { BasicButton } from "components/buttons/basic-button";

import { IAssistantPanelProps } from "./types";

export const StyledAssistantPanel = styled(Paper, {
  shouldForwardProp: (prop) => prop !== "panelPosition" && prop !== "panelSize",
})<Pick<IAssistantPanelProps, "panelPosition" | "panelSize">>(
  ({ theme, panelPosition, panelSize }) => ({
    position: "fixed",
    boxSizing: "border-box",
    right: theme.spacing(4),
    bottom: theme.spacing(4),
    ...(panelPosition && {
      left: panelPosition.left,
      top: panelPosition.top,
      right: "auto",
      bottom: "auto",
    }),
    width: `min(${panelSize.width}px, calc(100vw - ${theme.spacing(
      4
    )} - ${theme.spacing(4)}))`,
    // eslint-disable-next-line spellcheck/spell-checker -- Dynamic viewport units keep the composer visible on mobile.
    height: `min(${panelSize.height}px, calc(100dvh - ${theme.spacing(
      4
    )} - ${theme.spacing(4)}))`,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    border: `1px solid ${theme.palette.custom.semantic.border.default}`,
    borderRadius: theme.shape.borderRadius[3],
    boxShadow: theme.customShadows.lg,
    zIndex: theme.zIndex.modal + 2,
    color: theme.palette.primary.main,
    backgroundColor: theme.palette.custom.semantic.surface.card,
  })
);

export const AssistantHeader = styled(Box, {
  shouldForwardProp: (prop) => prop !== "dragging",
})<{ dragging: boolean }>(({ theme, dragging }) => ({
  cursor: dragging ? "grabbing" : "grab",
  touchAction: "none",
  userSelect: "none",
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(3),
  padding: theme.spacing(4, 5),
  flexShrink: 0,
  borderBottom: `1px solid ${theme.palette.custom.semantic.border.subtle}`,
}));

export const AssistantAvatar = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: theme.spacing(7),
  height: theme.spacing(7),
  flexShrink: 0,
  borderRadius: theme.shape.borderRadius[2],
  backgroundColor: alpha(
    theme.palette.secondary.main,
    theme.palette.action.focusOpacity
  ),
}));

export const AssistantCloseButton = styled(BasicButton)(({ theme }) => ({
  marginLeft: "auto",
  width: theme.spacing(7),
  minWidth: theme.spacing(7),
  height: theme.spacing(7),
  padding: theme.spacing(1),
  flexShrink: 0,
  borderRadius: theme.shape.borderRadius[1],
}));

export const AssistantMoveButton = styled(BasicButton)(({ theme }) => ({
  cursor: "inherit",
  minWidth: theme.spacing(5),
  width: theme.spacing(5),
  padding: 0,
  flexShrink: 0,
}));

export const AssistantResizeButton = styled(BasicButton)(({ theme }) => ({
  position: "absolute",
  top: theme.spacing(1),
  left: theme.spacing(1),
  minWidth: theme.spacing(4),
  width: theme.spacing(4),
  height: theme.spacing(4),
  padding: theme.spacing(1),
  // eslint-disable-next-line spellcheck/spell-checker -- Standard CSS diagonal resize cursor.
  cursor: "nwse-resize",
  touchAction: "none",
  color: theme.palette.custom.semantic.text.muted,
  borderRadius: theme.shape.borderRadius[1],
  zIndex: 1,
  "&:hover, &.Mui-focusVisible": {
    color: theme.palette.primary.main,
    backgroundColor: theme.palette.custom.semantic.surface.hover,
  },
}));

export const StyledAssistantPanelResizeIcon = styled(ResizeIcon)({
  transform: "rotate(-45deg)",
});
